/**
 * PartnershipProductAccess - manage which products a partnership can access.
 *
 * Fetches the current product grants via ACL and provides a dialog for
 * the tenant admin to bulk-set access.
 */

import { useState, useMemo } from 'react'
import { toast } from 'sonner'
import { Loader2, Package } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { ProductAccessPicker } from '@/admin/components/shared/product-access-picker'
import {
  usePartnershipProductAccess,
  useUpdatePartnershipProductAccess,
} from '@/admin/hooks/use-partnership-product-access'
import { useOrg } from '@/admin/hooks/use-org'

import { toastApiError } from '@/shared/lib/api-error'
interface PartnershipProductAccessProps {
  partnershipUuid: string
}

export function PartnershipProductAccess({
  partnershipUuid,
}: PartnershipProductAccessProps) {
  const { data, isLoading } = usePartnershipProductAccess(partnershipUuid)
  const mutation = useUpdatePartnershipProductAccess(partnershipUuid)
  // Product-access assignment is an upstream-admin action. In partner
  // (dealer) context the list is read-only - they see what they've been
  // granted but can't re-assign (the endpoint also 403s them).
  const { isPartnerAccess } = useOrg()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const grantedProducts = data?.granted_products ?? []
  const availableProducts = useMemo(
    () => data?.available_products ?? [],
    [data?.available_products]
  )

  function openDialog() {
    setSelected(new Set(grantedProducts.map((p) => p.uuid)))
    setSearch('')
    setDialogOpen(true)
  }

  function toggleProduct(uuid: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) {
        next.delete(uuid)
      } else {
        next.add(uuid)
      }
      return next
    })
  }

  function handleSelectAll() {
    setSelected(new Set(availableProducts.map((p) => p.uuid)))
  }

  function handleClearAll() {
    setSelected(new Set())
  }

  async function handleSave() {
    try {
      await mutation.mutateAsync(Array.from(selected))
      toast.success('Product access updated.')
      setDialogOpen(false)
    } catch (err) {
      toastApiError(err, 'Failed to update product access.');
    }
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="clara-section-label">Product Access</h3>
          {grantedProducts.length > 0 && (
            <span className="rounded-full bg-[var(--green-bg)] px-2 py-0.5 text-[9px] font-normal uppercase tracking-[0.1em] text-[var(--green)]">
              {grantedProducts.length}
            </span>
          )}
        </div>
        {!isPartnerAccess && (
          <Button
            variant="outline"
            size="sm"
            onClick={openDialog}
            className="cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-2)] transition-all duration-150 hover:border-[var(--green)] hover:text-[var(--green)]"
          >
            Manage
          </Button>
        )}
      </div>
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-[var(--ink-3)]" />
        </div>
      ) : grantedProducts.length === 0 ? (
        <div className="max-w-[480px] rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
          <p className="font-display text-[15px] text-[var(--ink-3)]">
            No products granted yet. Click Manage to configure access.
          </p>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-4 border-b-[0.5px] border-b-[var(--rule-strong)] pb-3">
            <span className="clara-th">Product</span>
            <span className="clara-th">Category</span>
            <span className="clara-th">Status</span>
          </div>
          {grantedProducts.map((p) => (
            <div
              key={p.uuid}
              className="grid grid-cols-[1fr_1fr_auto] items-center gap-4 border-b-[0.5px] border-b-[var(--rule)] py-3"
            >
              <div className="flex items-center gap-2">
                <Package className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                <p className="text-[13px] text-[var(--ink)]">
                  {p.name || 'Unnamed product'}
                </p>
              </div>
              <p className="text-[13px] text-[var(--ink-2)]">
                {p.category || '-'}
              </p>
              <span
                className={`rounded-[3px] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] ${
                  p.status === 'PUBLISHED'
                    ? 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
                    : 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
                }`}
              >
                {p.status}
              </span>
            </div>
          ))}
        </div>
      )}
      {/* Manage dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-full max-w-lg">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-[15px] text-[var(--ink)]">
              Manage Product Access
            </DialogTitle>
          </DialogHeader>

          <ProductAccessPicker
            availableProducts={availableProducts}
            selected={selected}
            onToggle={toggleProduct}
            onSelectAll={handleSelectAll}
            onClearAll={handleClearAll}
            search={search}
            onSearch={setSearch}
          />

          <DialogFooter className="mt-4 flex justify-end gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-3">
            <Button
              variant="outline"
              type="button"
              onClick={() => setDialogOpen(false)}
              className="cursor-pointer border-none bg-transparent px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={mutation.isPending}
              className="cursor-pointer rounded-[4px] border-none bg-[var(--green)] px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 disabled:opacity-50 hover:bg-[var(--green-hover)]"
            >
              {mutation.isPending && (
                <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
